import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSongs } from '../context/SongContext';
import { useUser } from '../context/UserContext';
import { useData } from '../context/DataContext';
import {
  Play, Pause, SkipBack, SkipForward, VolumeX, Volume2,
  Mic, Settings, Heart, Music, BarChart2, ArrowLeft,
  MessageSquare, Send, X, User
} from 'lucide-react';

export const KaraokePlayer: React.FC = () => {
  const { songId } = useParams<{ songId: string }>();
  const { songs, favorites, toggleFavorite } = useSongs();
  const { user } = useUser();
  const { socialService } = useData();
  
  const song = songs.find(s => s.id === songId);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [guideVocalLevel, setGuideVocalLevel] = useState(50);
  const [key, setKey] = useState(0);
  const [tempo, setTempo] = useState(100);
  const [isRecording, setIsRecording] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState({
    likes: false,
    comments: false,
  });
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isPlaying) {
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            setIsPlaying(false);
            return 0;
          }
          return prev + 0.5;
        });
      }, 1000);
    }
    
    return () => clearInterval(interval);
  }, [isPlaying]);

  useEffect(() => {
    if (songId && user) {
      loadSocialData();
    }
  }, [songId, user]);

  const loadSocialData = async () => {
    if (!songId || !user) return;

    try {
      // Load like count
      setLoading(prev => ({ ...prev, likes: true }));
      const count = await socialService.getLikes(songId);
      const userLiked = await socialService.isLiked(user.id, songId);
      setLikeCount(count);
      setIsLiked(userLiked);
      setLoading(prev => ({ ...prev, likes: false }));

      // Load comments
      setLoading(prev => ({ ...prev, comments: true }));
      const commentsList = await socialService.getComments(songId);
      setComments(commentsList);
      setLoading(prev => ({ ...prev, comments: false }));
    } catch (err) {
      console.error('Error loading social data:', err);
      setError('Failed to load social data');
      setLoading({
        likes: false,
        comments: false,
      });
    }
  };

  const handleLikeToggle = async () => {
    if (!user || !songId) return;

    try {
      setLoading(prev => ({ ...prev, likes: true }));
      if (isLiked) {
        await socialService.unlikePerformance(user.id, songId);
        setLikeCount(prev => Math.max(prev - 1, 0));
      } else {
        await socialService.likePerformance(user.id, songId);
        setLikeCount(prev => prev + 1);
      }
      setIsLiked(!isLiked);
    } catch (err) {
      console.error('Error toggling like:', err);
      setError('Failed to process like');
    } finally {
      setLoading(prev => ({ ...prev, likes: false }));
    }
  };

  const handleAddComment = async () => {
    if (!user || !songId || !commentText.trim()) return;

    try {
      setLoading(prev => ({ ...prev, comments: true }));
      const newComment = await socialService.addComment(user.id, songId, commentText);
      
      // Add user info to the comment for display
      const enrichedComment = {
        ...newComment,
        username: user.name,
        avatar_url: user.avatar
      };
      
      setComments(prev => [enrichedComment, ...prev]);
      setCommentText('');
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment');
    } finally {
      setLoading(prev => ({ ...prev, comments: false }));
    }
  };
  
  if (!song) {
    return <div className="p-8 text-center text-white">Song not found</div>;
  }
  
  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };
  
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };
  
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setVolume(value);
    if (value === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
  };
  
  const handleGuideVocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGuideVocalLevel(parseInt(e.target.value));
  };
  
  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKey(parseInt(e.target.value));
  };
  
  const handleTempoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempo(parseInt(e.target.value));
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const currentTime = (song.duration * progress) / 100;
  
  return (
    <div className="container mx-auto p-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-500">
          {error}
        </div>
      )}
      
      <div className="flex flex-col md:flex-row gap-6">
        {/* Main Player Section */}
        <div className="md:w-2/3">
          <div className="card mb-6">
            <div className="flex items-center mb-6">
              <Link to="/kod-studio" className="p-2 mr-2 hover:bg-kod-purple-700 rounded-full text-white/70 hover:text-white">
                <ArrowLeft size={20} />
              </Link>
              <h1 className="text-2xl font-bold">Now Playing</h1>
            </div>
            
            {/* Song Info */}
            <div className="flex items-center mb-8">
              <img src={song.coverArt} alt={song.title} className="w-20 h-20 rounded-lg object-cover mr-4" />
              <div>
                <h2 className="text-xl font-bold">{song.title}</h2>
                <p className="text-white/70">{song.artist}</p>
                <div className="flex items-center mt-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    song.difficulty === 'Easy' ? 'bg-green-900/30 text-green-300' :
                    song.difficulty === 'Medium' ? 'bg-blue-900/30 text-blue-300' :
                    song.difficulty === 'Hard' ? 'bg-orange-900/30 text-orange-300' :
                    'bg-red-900/30 text-red-300'
                  }`}>
                    {song.difficulty}
                  </span>
                  <span className="ml-2 text-sm text-white/50">{song.genre} • {song.releaseYear}</span>
                </div>
              </div>
              <div className="ml-auto flex items-center space-x-2">
                <button 
                  className="p-2 rounded-full hover:bg-kod-purple-700 transition-colors text-white/70 hover:text-white"
                  onClick={() => setShowComments(true)}
                >
                  <div className="relative">
                    <MessageSquare size={24} />
                    <span className="absolute -top-1 -right-1 bg-kod-accent rounded-full text-xs font-bold h-4 w-4 flex items-center justify-center">
                      {comments.length}
                    </span>
                  </div>
                </button>
                <button 
                  onClick={handleLikeToggle}
                  className="p-2 rounded-full hover:bg-kod-purple-700 transition-colors text-white/70 hover:text-white"
                  disabled={loading.likes}
                >
                  <div className="relative">
                    <Heart 
                      size={24} 
                      fill={isLiked ? 'currentColor' : 'none'} 
                      className={isLiked ? 'text-kod-accent' : ''}
                    />
                    <span className="absolute -top-1 -right-1 bg-kod-accent rounded-full text-xs font-bold h-4 w-4 flex items-center justify-center">
                      {likeCount}
                    </span>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Lyrics Display */}
            <div className="bg-kod-purple-900 rounded-lg p-6 mb-6 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-kod-accent/20 to-purple-500/20"></div>
              <div className="relative z-10">
                <p className="text-white/50 text-lg mb-2">Coming up...</p>
                <p className="text-white/80 text-xl mb-4">I've been there all night</p>
                <p className="text-white text-3xl font-bold mb-4">I've been there all day</p>
                <p className="text-white/80 text-xl mb-2">And boy, got me walkin' side to side</p>
                <p className="text-white/50 text-lg">I've been there all night, I've been there all day</p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm text-white/60 mb-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(song.duration)}</span>
              </div>
              <div className="h-2 bg-kod-purple-700 rounded-full">
                <div 
                  className="h-2 bg-kod-accent rounded-full"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
            
            {/* Player Controls */}
            <div className="flex justify-center items-center space-x-6 mb-6">
              <button className="p-2 text-white/60 hover:text-white transition-colors">
                <SkipBack size={28} />
              </button>
              <button 
                onClick={togglePlay}
                className="p-4 bg-kod-accent text-white rounded-full hover:bg-kod-accent-dark transition-colors"
              >
                {isPlaying ? <Pause size={32} /> : <Play size={32} fill="currentColor" />}
              </button>
              <button className="p-2 text-white/60 hover:text-white transition-colors">
                <SkipForward size={28} />
              </button>
            </div>
            
            {/* Volume Controls */}
            <div className="flex items-center space-x-4 mb-2">
              <button onClick={toggleMute} className="text-white/60 hover:text-white">
                {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
              <div className="flex-1">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-full h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                />
              </div>
              <span className="text-sm text-white/60 w-8 text-right">{isMuted ? 0 : volume}%</span>
            </div>
          </div>
        </div>
        
        {/* Sidebar Controls */}
        <div className="md:w-1/3">
          {/* Recording Controls */}
          <div className="card mb-6">
            <h2 className="subtitle">Recording</h2>
            <div className="flex items-center justify-between mb-6">
              <button 
                onClick={() => setIsRecording(!isRecording)}
                className={`px-4 py-2 rounded-lg flex items-center ${
                  isRecording ? 'bg-kod-accent/20 text-kod-accent' : 'bg-kod-purple-700 text-white'
                }`}
              >
                <Mic size={18} className="mr-2" />
                {isRecording ? 'Stop Recording' : 'Start Recording'}
              </button>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-white/60">Guide</span>
                <div className="w-24">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={guideVocalLevel}
                    onChange={handleGuideVocalChange}
                    className="w-full h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                  />
                </div>
                <span className="text-sm text-white/60 w-8 text-right">{guideVocalLevel}%</span>
              </div>
            </div>
            
            <div className={`p-4 rounded-lg ${isRecording ? 'bg-kod-accent/10 border border-kod-accent/20' : 'bg-kod-purple-700/50 border border-kod-purple-600/30'} mb-4`}>
              {isRecording ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-kod-accent rounded-full animate-pulse mr-2"></div>
                    <span className="text-kod-accent font-medium">Recording in progress...</span>
                  </div>
                  <div className="text-sm text-white/60">00:42</div>
                </div>
              ) : (
                <div className="flex items-center justify-center text-white/50">
                  <Mic size={18} className="mr-2" />
                  <span>Ready to record</span>
                </div>
              )}
            </div>
            
            <div className="text-sm text-white/70 mb-2">Microphone effects:</div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <button className="p-2 bg-kod-purple-700/50 border border-kod-purple-600/50 rounded-md text-white/70 hover:bg-kod-purple-600/50 hover:text-white transition-colors text-sm">
                Reverb
              </button>
              <button className="p-2 bg-kod-accent/20 border border-kod-accent/30 text-kod-accent rounded-md text-sm font-medium">
                Auto-Tune
              </button>
              <button className="p-2 bg-kod-purple-700/50 border border-kod-purple-600/50 rounded-md text-white/70 hover:bg-kod-purple-600/50 hover:text-white transition-colors text-sm">
                Echo
              </button>
            </div>
          </div>
          
          {/* Playback Settings */}
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="subtitle mb-0">Playback Settings</h2>
              <button className="p-1 text-white/50 hover:text-white">
                <Settings size={18} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-white/70">Key</span>
                  <span className="text-sm font-medium">{key > 0 ? `+${key}` : key}</span>
                </div>
                <input
                  type="range"
                  min="-6"
                  max="6"
                  value={key}
                  onChange={handleKeyChange}
                  className="w-full h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                />
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-white/70">Tempo</span>
                  <span className="text-sm font-medium">{tempo}%</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={tempo}
                  onChange={handleTempoChange}
                  className="w-full h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                />
              </div>
            </div>
          </div>
          
          {/* Performance Actions */}
          <div className="grid grid-cols-2 gap-4">
            <button className="btn-outline flex items-center justify-center">
              <BarChart2 size={18} className="mr-2" />
              AI Analysis
            </button>
            <button className="btn-primary flex items-center justify-center">
              <Music size={18} className="mr-2" />
              Save & Share
            </button>
          </div>
        </div>
      </div>

      {/* Comments Modal */}
      {showComments && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Comments ({comments.length})</h2>
              <button 
                onClick={() => setShowComments(false)}
                className="p-2 hover:bg-kod-purple-700 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Comment Input */}
            <div className="flex space-x-2 mb-4">
              <img 
                src={user?.avatar || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80"} 
                alt="User" 
                className="w-8 h-8 rounded-full object-cover"
              />
              <div className="flex-1 flex bg-kod-purple-700 rounded-lg overflow-hidden">
                <input
                  type="text"
                  className="flex-1 bg-transparent border-none px-3 py-2 focus:outline-none text-white"
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <button 
                  className="px-3 text-kod-accent hover:bg-kod-purple-600"
                  onClick={handleAddComment}
                  disabled={loading.comments || !commentText.trim()}
                >
                  {loading.comments ? 
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-kod-accent"></div> :
                    <Send size={18} />
                  }
                </button>
              </div>
            </div>
            
            {/* Comments List */}
            <div className="flex-1 overflow-y-auto space-y-4">
              {comments.length > 0 ? (
                comments.map((comment, index) => (
                  <div key={comment.id || index} className="flex space-x-2">
                    {comment.avatar_url ? (
                      <img
                        src={comment.avatar_url}
                        alt="User"
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <User size={16} className="text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="bg-kod-purple-700/70 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div className="font-medium">{comment.username || "User"}</div>
                          <div className="text-xs text-white/50">
                            {new Date(comment.created_at).toLocaleDateString()} 
                          </div>
                        </div>
                        <p className="text-white/90 mt-1">{comment.comment}</p>
                      </div>
                      <div className="flex mt-1 space-x-4 text-xs text-white/50 px-2">
                        <button className="hover:text-white">Like</button>
                        <button className="hover:text-white">Reply</button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-white/50">
                  <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};